<script setup lang="ts">
import arrowIcon from '../../assets/images/tooltip-arrow.svg'

type PositionX = 'left' | 'center' | 'right'

type PositionY = 'top' | 'center' | 'bottom'

type Props = {
  active?: boolean
  positionX?: PositionX
  positionY?: PositionY
  width?: number | string
  withIcon?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  positionX: 'right',
  positionY: 'bottom',
  width: 200,
})

const componentClasses = computed(() => {
  const classes = [
    'app-tooltip__content',
    `app-tooltip__content--x-${props.positionX}`,
    `app-tooltip__content--y-${props.positionY}`,
  ]

  if (props.active) classes.push('app-tooltip__content--active')

  return classes
})

const componentStyles = computed(() => {
  return {
    width: `${props.width}px`,
  }
})
</script>

<template>
  <div class="app-tooltip">
    <span v-if="$slots.activator" class="app-tooltip__activator">
      <slot name="activator" />
    </span>

    <div
      v-if="$slots.default"
      :class="componentClasses"
      :style="componentStyles"
    >
      <img
        v-if="withIcon"
        class="app-tooltip__icon"
        :src="arrowIcon"
        alt="Иконка для стрелки"
      />

      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-tooltip {
  position: relative;

  &__icon {
    position: absolute;
    pointer-events: none;
  }

  &__content {
    position: absolute;
    z-index: 2;
    padding: 16px;
    color: var(--ui-white);
    pointer-events: none;
    visibility: hidden;
    background-color: rgba(var(--ui-secondary-rgb), 0.9);
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

    &--active {
      pointer-events: auto;
      visibility: visible;
      opacity: 1;
    }

    /* Horizontal positions */
    &--x-left {
      right: 0;

      .app-tooltip__icon {
        right: 32px;
      }
    }

    &--x-center {
      left: 50%;
      transform: translateX(-50%);

      .app-tooltip__icon {
        left: calc(50% - 8px);
      }
    }

    &--x-right {
      left: 0;

      .app-tooltip__icon {
        left: 32px;
      }
    }

    /* Vertical positions */
    &--y-top {
      bottom: calc(100% + 10px);

      .app-tooltip__icon {
        bottom: -16px;
        transform: rotate(180deg);
      }
    }

    &--y-center {
      bottom: 50%;
      transform: translateY(50%);

      .app-tooltip__icon {
        top: calc(50% - 8px);
      }
    }

    &--y-bottom {
      top: calc(100% + 10px);

      .app-tooltip__icon {
        top: -16px;
      }
    }

    /* Both positions */
    &--x-left#{&}--y-center {
      right: calc(100% + 10px);

      .app-tooltip__icon {
        right: -16px;
        transform: rotate(90deg);
      }
    }

    &--x-center#{&}--y-center {
      transform: translate(-50%, 50%);

      .app-tooltip__icon {
        display: none;
      }
    }

    &--x-right#{&}--y-center {
      left: calc(100% + 10px);

      .app-tooltip__icon {
        left: -16px;
        transform: rotate(270deg);
      }
    }
  }

  &:hover,
  &:focus,
  &:focus-within {
    .app-tooltip__content {
      pointer-events: auto;
      visibility: visible;
      opacity: 1;
    }
  }
}
</style>
